import React, { useState, useEffect } from "react";
import { Layout, Table, Button, Card, message, Space, Modal, Descriptions, Tag, Spin, Input, Row, Col } from "antd";
import { SafetyCertificateOutlined, CheckOutlined, CloseOutlined, EyeOutlined, SearchOutlined } from "@ant-design/icons";
import api from "../../../services/axiosInstance";
import dayjs from "dayjs";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import "../../../styles/fluidDesign.css";
import AppBreadcrumb from "../../../components/common/AppBreadcrumb";
import { getStatusColor } from "../../../utils/statusHelper";

const { Content } = Layout;

const ReadyItems = () => {
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedNote, setSelectedNote] = useState(null);
    const [reviewVisible, setReviewVisible] = useState(false);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [search, setSearch] = useState("");

    useEffect(() => {
        fetchPendingNotes();
    }, []);

    const fetchPendingNotes = async () => {
        try {
            setLoading(true);
            const response = await api.get("/inventory/issuing/list.php?iin_status=Preparing");
            if (response.data.status === "success") {
                setNotes(response.data.dataset || []);
            } else {
                message.error("Failed to fetch prepared issue notes.");
            }
        } catch (error) {
            message.error("Error connecting to issuing API.");
        } finally {
            setLoading(false);
        }
    };

    const handleOpenReview = async (record) => {
        try {
            setLoadingDetail(true);
            setReviewVisible(true);
            const response = await api.get(`/inventory/issuing/list.php?iin_id=${record.iin_id}`);
            if (response.data.status === "success") {
                setSelectedNote(response.data.dataset);
            } else {
                message.error("Failed to load details.");
                setReviewVisible(false);
            }
        } catch (error) {
            message.error("Error loading details.");
            setReviewVisible(false);
        } finally {
            setLoadingDetail(false);
        }
    };

    // Client-side search over the loaded notes
    const filteredNotes = search.trim()
        ? notes.filter((n) => {
            const q = search.trim().toLowerCase();
            return (
                (n.iin_ref || "").toLowerCase().includes(q) ||
                (n.mr_ref || "").toLowerCase().includes(q) ||
                (n.prepare_user_dname || "").toLowerCase().includes(q)
            );
        })
        : notes;

    const handleAction = async (status) => {
        if (!selectedNote) return;
        try {
            setSubmitting(true);
            const response = await api.post("/inventory/issuing/approve.php", {
                iin_id: Number(selectedNote.iin_id),
                status: status
            });
            if (response.data.status === "success") {
                message.success(response.data.message || "Issue note successfully approved.");
                setReviewVisible(false);
                setSelectedNote(null);
                fetchPendingNotes();
            } else {
                message.error(response.data.message || "Action failed.");
            }
        } catch (error) {
            message.error("Error processing approval.");
        } finally {
            setSubmitting(false);
        }
    };

    const columns = [
        {
            title: "Reference",
            dataIndex: "iin_ref",
            key: "iin_ref",
            render: (text) => <strong style={{ color: 'var(--fluid-primary)' }}>{text}</strong>
        },
        {
            title: "MR Ref",
            dataIndex: "mr_ref",
            key: "mr_ref",
        },
        {
            title: "Prepared By",
            dataIndex: "prepare_user_dname",
            key: "prepare_user_dname",
            render: (text) => text || "-"
        },
        {
            title: "Prepared Date",
            dataIndex: "prepare_dt",
            key: "prepare_dt",
            render: (date) => date ? dayjs(date).format("YYYY-MM-DD hh:mm A") : "-"
        },
        {
            title: "Items Count",
            dataIndex: "total_items",
            key: "total_items",
            align: 'center'
        },
        {
            title: "Total Qty",
            dataIndex: "total_qty",
            key: "total_qty",
            align: 'right',
            render: (qty) => parseFloat(qty || 0).toFixed(2)
        },
        {
            title: "Action",
            key: "action",
            align: 'center',
            render: (_, record) => (
                <Button
                    type="primary"
                    className="btn-review"
                    icon={<EyeOutlined />}
                    size="small"
                    style={{ borderRadius: '6px', height: '28px' }}
                    onClick={() => handleOpenReview(record)}
                >
                    Review
                </Button>
            )
        }
    ];

    const detailColumns = [
        {
            title: "Item Code",
            dataIndex: "in_code",
            key: "in_code"
        },
        {
            title: "Item Name",
            dataIndex: "in_name",
            key: "in_name"
        },
        {
            title: "UOM",
            dataIndex: "uom_name",
            key: "uom_name",
            render: (text) => text || "-"
        },
        {
            title: "Issue Qty",
            dataIndex: "issuenote_qty",
            key: "issuenote_qty",
            align: 'right',
            render: (qty) => parseFloat(qty || 0).toFixed(2)
        },
        {
            title: "Warehouse Stock",
            dataIndex: "current_stock",
            key: "current_stock",
            align: 'right',
            render: (qty) => parseFloat(qty || 0).toFixed(2)
        },
        {
            title: "Serial Numbers",
            dataIndex: "serial_numbers",
            key: "serial_numbers",
            render: (text) => text || "-"
        },
        {
            title: "Special Details",
            dataIndex: "special_details",
            key: "special_details",
            render: (text) => text || "-"
        }
    ];

    return (
        <Layout className="fluid-page">
            <Headerdiv />

            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <AppBreadcrumb items={[
                    { label: 'Inventory', link: '/inventory-items' },
                    { label: 'Ready for Issue' },
                ]} />
                <h1 className="fluid-page-title">
                    <SafetyCertificateOutlined style={{ marginRight: 12 }} />Ready for Issue
                </h1>
                
            </div>

            <Content className="fluid-content">
                <Card className="fluid-card">
                    <div style={{ marginBottom: 12 }}>
                        <Row gutter={16} align="bottom" className="fluid-filter-row">
                            <Col xs={24} sm={12} md={8} lg={6}>
                                <div style={{ marginBottom: 6, fontWeight: 500, color: "#595959" }}>Search:</div>
                                <Input
                                    placeholder="Search ref, MR ref, prepared by"
                                    prefix={<SearchOutlined />}
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    allowClear
                                />
                            </Col>
                        </Row>
                    </div>
                    <Table
                        columns={columns}
                        dataSource={filteredNotes}
                        rowKey="iin_id"
                        loading={loading}
                        bordered
                        size="middle"
                        scroll={{ x: 'max-content' }}
                        pagination={{
                            defaultPageSize: 10,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`
                        }}
                    />
                </Card>
            </Content>

            {/* Review Modal */}
            <Modal
                title={`Review Issue Note: ${selectedNote ? selectedNote.iin_ref : ""}`}
                open={reviewVisible}
                onCancel={() => {
                    setReviewVisible(false);
                    setSelectedNote(null);
                }}
                footer={[
                    <Button 
                        key="close" 
                        onClick={() => setReviewVisible(false)}
                        disabled={submitting}
                    >
                        Close
                    </Button>,
                    <Button 
                        key="approve" 
                        type="primary" 
                        icon={<CheckOutlined />} 
                        onClick={() => handleAction("Approved")}
                        loading={submitting}
                        style={{ background: 'var(--fluid-primary)', borderColor: 'var(--fluid-primary)' }}
                    >
                        Approve
                    </Button>
                ]}
                width={900}
                destroyOnClose
            >
                {loadingDetail || !selectedNote ? (
                    <div style={{ textAlign: 'center', padding: '30px 0' }}>
                        <Spin size="large" tip="Loading details..." />
                    </div>
                ) : (
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        <Descriptions bordered column={2} size="small">
                            <Descriptions.Item label="Reference"><strong>{selectedNote.iin_ref}</strong></Descriptions.Item>
                            <Descriptions.Item label="Status"><Tag color={getStatusColor(selectedNote.iin_status)}>{selectedNote.iin_status}</Tag></Descriptions.Item>
                            <Descriptions.Item label="MR Ref">{selectedNote.mr_ref}</Descriptions.Item>
                             <Descriptions.Item label="Prepared By">{selectedNote.prepare_user_dname || "-"}</Descriptions.Item>
                             <Descriptions.Item label="Prepared Date">
                                 {selectedNote.prepare_dt ? dayjs(selectedNote.prepare_dt).format("YYYY-MM-DD hh:mm A") : "-"}
                             </Descriptions.Item>
                        </Descriptions>

                        <Table
                            dataSource={selectedNote.items}
                            columns={detailColumns}
                            rowKey="iinitem_id"
                            pagination={false}
                            bordered
                            size="middle"
                        />
                    </Space>
                )}
            </Modal>

            <Footerdiv />
        </Layout>
    );
};

export default ReadyItems;
